import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config';
import './ConsultationPage.css';
import consultationHero from '../assets/images/consultation-hero.webp';

const FAQ_DATA = [
  {
    q: 'مشاوره مدیریت چیست؟',
    a: 'مشاوره مدیریت فرآیند کمک به سازمان‌ها برای بهبود عملکرد، حل مسائل سازمانی و دستیابی به اهداف استراتژیک است. یک مشاور مدیریت با تحلیل وضعیت فعلی سازمان، نقاط ضعف و قوت را شناسایی و راهکارهای عملی ارائه می‌دهد.',
  },
  {
    q: 'چرا به مشاوره مدیریت نیاز داریم؟',
    a: 'سازمان‌ها در مسیر رشد با چالش‌هایی مانند کاهش بهره‌وری، تعارضات سازمانی، عدم شفافیت استراتژیک و نیاز به تحول دیجیتال مواجه می‌شوند. مشاوره مدیریت با ارائه دیدگاه بیرونی و تجربه تخصصی، مسیر درست را نشان می‌دهد.',
  },
  {
    q: 'مشاوره مدیریت شامل چه حوزه‌هایی می‌شود؟',
    a: 'حوزه‌های اصلی مشاوره مدیریت شامل استراتژی کسب‌وکار، برندینگ و بازاریابی، تحول سازمانی، توسعه سرمایه انسانی، تحول دیجیتال، طراحی سازمانی و بین‌المللی‌سازی کسب‌وکار می‌شود.',
  },
  {
    q: 'فرآیند مشاوره مدیریت در زوپین چگونه است؟',
    a: 'فرآیند مشاوره در زوپین شامل ۴ مرحله است: ۱) عارضه‌یابی و تحلیل وضعیت فعلی، ۲) طراحی راهکار استراتژیک، ۳) همراهی در اجرا، و ۴) سنجش نتایج و بهبود مستمر. ما از مرحله ایده تا اجرا در کنار شما هستیم.',
  },
  {
    q: 'هزینه مشاوره مدیریت چقدر است؟',
    a: 'هزینه مشاوره بسته به نوع پروژه، حوزه تخصصی و مدت زمان همکاری متفاوت است. برای دریافت اطلاعات دقیق و مشاوره اولیه رایگان، فرم زیر را تکمیل کنید تا کارشناسان ما با شما تماس بگیرند.',
  },
];

export default function ConsultationPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', company: '', phone: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/consultation/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'خطا در ارسال');
      }

      navigate('/consultation/success');
    } catch (err) {
      setError(err.message || 'خطا در ارسال فرم. لطفاً دوباره تلاش کنید.');
    } finally {
      setLoading(false);
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_DATA.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'مشاوره مدیریت',
    provider: { '@type': 'Organization', name: 'زوپین', url: 'https://zoopin.co' },
    description: 'خدمات تخصصی مشاوره مدیریت و توسعه سازمانی شامل استراتژی کسب‌وکار، تحول سازمانی و توسعه سرمایه انسانی',
    areaServed: { '@type': 'Country', name: 'ایران' },
    url: 'https://zoopin.co/consultation',
  };

  return (
    <div className="consultation-page">
      <Helmet>
        <title>درخواست مشاوره مدیریت - زوپین</title>
        <meta name="description" content="مشاوره مدیریت تخصصی زوپین: استراتژی کسب‌وکار، تحول سازمانی، توسعه سرمایه انسانی و بین‌المللی‌سازی. فرم درخواست مشاوره رایگان را تکمیل کنید." />
        <link rel="canonical" href="https://zoopin.co/consultation" />
        <meta property="og:title" content="درخواست مشاوره مدیریت - زوپین" />
        <meta property="og:description" content="خدمات تخصصی مشاوره مدیریت و توسعه سازمانی زوپین. فرم درخواست مشاوره رایگان." />
        <meta property="og:url" content="https://zoopin.co/consultation" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content="درخواست مشاوره مدیریت - زوپین" />
        <meta name="twitter:description" content="خدمات تخصصی مشاوره مدیریت و توسعه سازمانی زوپین." />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>

      {/* Hero Section */}
      <section
        className="lc-hero"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0.65)), url(${consultationHero})`,
        }}
      >
        <div className="lc-hero-inner">
          <h1 className="lc-hero-title">مشاوره مدیریت و توسعه سازمانی</h1>
          <p className="lc-hero-sub">
            زوپین با ترکیب بهترین تجربه‌های جهانی و شناخت عمیق از بازار ایران، راهکارهای عملی و دادهمحور برای رشد پایدار سازمان شما ارائه می‌دهد.
          </p>
          <a href="#consultation-form" className="lc-hero-cta">درخواست مشاوره رایگان</a>
        </div>
      </section>

      {/* Services Section */}
      <section className="lc-section">
        <h2 className="lc-section-title">حوزه‌های مشاوره مدیریت</h2>
        <div className="lc-services-grid">
          <div className="lc-service-card">
            <h3>استراتژی کسب‌وکار</h3>
            <p>طراحی و بازنگری استراتژی سازمان، تدوین نقشه راه رشد و تعیین مسیر توسعه بلندمدت.</p>
          </div>
          <div className="lc-service-card">
            <h3>تحول سازمانی</h3>
            <p>برنامه‌ریزی و اجرای تغییرات ساختاری، فرهنگی و فرآیندی برای افزایش بهره‌وری.</p>
          </div>
          <div className="lc-service-card">
            <h3>توسعه سرمایه انسانی</h3>
            <p>ارزیابی و تقویت تیم مدیریتی، طراحی سیستم‌های انگیزشی و توسعه مهارت‌ها.</p>
          </div>
          <div className="lc-service-card">
            <h3>برندینگ و بازاریابی</h3>
            <p>طراحی هویت برند، استراتژی بازاریابی دیجیتال و توسعه بازارهای هدف.</p>
          </div>
          <div className="lc-service-card">
            <h3>تحول دیجیتال</h3>
            <p>طراحی و اجرای فرآیندهای دیجیتال، انتخاب فناوری‌های مناسب و آموزش تیم‌ها.</p>
          </div>
          <div className="lc-service-card">
            <h3>بین‌المللی‌سازی</h3>
            <p>ورود به بازارهای بین‌المللی، صادرات و گسترش فعالیت‌های تجاری در منطقه.</p>
          </div>
        </div>
      </section>

      {/* Why Zoopin Section */}
      <section className="lc-section lc-why">
        <h2 className="lc-section-title">چرا زوپین؟</h2>
        <div className="lc-why-grid">
          <div className="lc-why-item">
            <strong>همراهی از ایده تا اجرا</strong>
            <p>برخلاف مشاورانی که فقط گزارش می‌دهند، زوپین در تمام مراحل اجرا در کنار شماست.</p>
          </div>
          <div className="lc-why-item">
            <strong>ترکیب جهانی و محلی</strong>
            <p>الگوهای جهانی مدیریت را با شناخت عمیق از بازار ایران ترکیب می‌کنیم.</p>
          </div>
          <div className="lc-why-item">
            <strong>تیم متخصص و باتجربه</strong>
            <p>مشاوران ما دارای تجربه عملی در صنایع مختلف و سوابق مدیریتی ارشد هستند.</p>
          </div>
          <div className="lc-why-item">
            <strong>نتیجه‌محور و قابل سنجش</strong>
            <p>اهداف قابل اندازه‌گیری تعیین می‌کنیم و عملکرد را به‌طور مستمر پایش می‌کنیم.</p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="lc-section lc-form-section" id="consultation-form">
        <div className="lc-form-wrap">
          <h2 className="lc-section-title">درخواست مشاوره مدیریت</h2>
          <p className="lc-form-sub">
            اطلاعات خود را وارد کنید تا کارشناسان زوپین در اسرع وقت با شما تماس بگیرند.
          </p>

          <form className="consultation-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">نام و نام خانوادگی *</label>
              <input type="text" id="name" name="name" value={form.name} onChange={handleChange} required placeholder="مثال: علی رضایی" />
            </div>
            <div className="form-group">
              <label htmlFor="company">نام شرکت</label>
              <input type="text" id="company" name="company" value={form.company} onChange={handleChange} placeholder="نام شرکت" />
            </div>
            <div className="form-group">
              <label htmlFor="phone">شماره تماس *</label>
              <input type="tel" id="phone" name="phone" value={form.phone} onChange={handleChange} required placeholder="0912XXXXXXX" dir="ltr" style={{ textAlign: 'right' }} />
            </div>
            <div className="form-group">
              <label htmlFor="email">ایمیل</label>
              <input type="email" id="email" name="email" value={form.email} onChange={handleChange} placeholder="example@company.com" dir="ltr" style={{ textAlign: 'right' }} />
            </div>
            <div className="form-group">
              <label htmlFor="message">توضیحات</label>
              <textarea id="message" name="message" value={form.message} onChange={handleChange} rows="4" placeholder="موضوع مشاوره، چالش سازمانی یا هر توضیح دیگر..." />
            </div>
            {error && <p className="form-error">{error}</p>}
            <button type="submit" className="form-submit" disabled={loading}>
              {loading ? 'در حال ارسال...' : 'ارسال درخواست مشاوره'}
            </button>
          </form>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="lc-section lc-faq">
        <h2 className="lc-section-title">سؤالات متداول درباره مشاوره مدیریت</h2>
        <div className="lc-faq-list">
          {FAQ_DATA.map((f, i) => (
            <div className="lc-faq-item" key={i}>
              <h3>{f.q}</h3>
              <p>{f.a}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
